import { useRef, useState, type PointerEvent } from 'react';
import { makeStyles } from '@material-ui/core';
import { useInsights } from '../catalog/pipeline/useInsights';
import { visuallyHidden } from '../visuallyHidden';
import type { HistoryDay } from './repositories';

type History = { kept: boolean; days: HistoryDay[] };

const W = 160;
const H = 32;
const PAD = 3;

const useStyles = makeStyles(theme => ({
  // As wide as the line at most, so a point's place in it is the tooltip's.
  box: { position: 'relative', marginTop: theme.spacing(0.75), maxWidth: 220 },
  svg: {
    display: 'block',
    width: '100%',
    maxWidth: 220,
    height: H,
    overflow: 'visible',
    cursor: 'crosshair',
    touchAction: 'none',
  },
  line: {
    fill: 'none',
    stroke: 'var(--ui-text-muted)',
    strokeWidth: 2,
    strokeLinejoin: 'round',
    strokeLinecap: 'round',
    vectorEffect: 'non-scaling-stroke',
  },
  hair: {
    stroke: 'var(--ui-border)',
    strokeWidth: 1,
    vectorEffect: 'non-scaling-stroke',
  },
  now: { fill: 'var(--ui-primary)' },
  // A ring of the tile's own colour, so the dot stands off the line.
  ring: { stroke: 'var(--ui-bg-elevated)', strokeWidth: 2 },
  tip: {
    position: 'absolute',
    bottom: H + 6,

    padding: theme.spacing(0.25, 0.75),
    borderRadius: 4,
    border: '1px solid var(--ui-border)',
    background: 'var(--ui-bg-elevated)',
    color: 'var(--ui-text-muted)',
    fontSize: '0.75rem',
    whiteSpace: 'nowrap',
    pointerEvents: 'none',
    zIndex: 1,
    '& b': { color: 'var(--ui-text)', fontWeight: 600 },
  },
  sub: { fontSize: '0.8rem', color: 'var(--ui-text-muted)' },
  hidden: visuallyHidden,
}));

/** "24 Oct", for a day kept as `2026-10-24`. */
const dayLabel = (day: string) =>
  new Date(`${day}T12:00:00Z`).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    timeZone: 'Europe/Berlin',
  });

const pct = (share: number) => `${Math.round(share * 100)}%`;

type Point = { day: string; met: number; of: number; share: number };

/**
 * The share of repositories meeting every team rule, day by day (DX-225,
 * specs/repository-standard.md AC-15): a sparkline in the figure's tile, in
 * the muted ink, today's point in the accent; a crosshair and a tooltip on
 * hover; and the same days as a table for a screen reader. One series, so it
 * needs no legend — the tile's label names it.
 */
export const StandardTrend = () => {
  const classes = useStyles();
  const history = useInsights<History>('/repositories/history');
  const svg = useRef<SVGSVGElement>(null);
  const [hover, setHover] = useState<number | null>(null);

  if (history.loading) return null;
  if (history.error || !history.data?.kept) {
    return (
      <div className={classes.sub}>No trend: the figures could not be kept</div>
    );
  }
  const points: Point[] = history.data.days
    .map(d => ({
      day: d.day,
      met: d.teamRules.met,
      of: d.teamRules.met + d.teamRules.notMet,
    }))
    .filter(p => p.of > 0)
    .map(p => ({ ...p, share: p.met / p.of }));

  if (points.length < 2) {
    return (
      <div className={classes.sub}>
        Kept day by day from{' '}
        {points.length ? dayLabel(points[0].day) : 'the next measurement'} on
      </div>
    );
  }

  // Anchored at zero, so a rise from 1 % to 3 % looks as small as it is.
  const top = Math.max(0.05, ...points.map(p => p.share)) * 1.1;
  const x = (i: number) => PAD + (i / (points.length - 1)) * (W - 2 * PAD);
  const y = (share: number) => H - PAD - (share / top) * (H - 2 * PAD);
  const path = points
    .map(
      (p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(p.share).toFixed(1)}`,
    )
    .join(' ');
  const first = points[0];
  const last = points[points.length - 1];
  const delta = Math.round((last.share - first.share) * 100);
  let change = `no change since ${dayLabel(first.day)}`;
  if (delta > 0) change = `▲ ${delta} pts since ${dayLabel(first.day)}`;
  if (delta < 0) change = `▼ ${-delta} pts since ${dayLabel(first.day)}`;

  // The crosshair snaps to the nearest day: readers aim at a date, not a line.
  const onMove = (e: PointerEvent<SVGSVGElement>) => {
    const box = svg.current?.getBoundingClientRect();
    if (!box || box.width === 0) return;
    const at = ((e.clientX - box.left) / box.width) * W;
    const i = Math.round(((at - PAD) / (W - 2 * PAD)) * (points.length - 1));
    setHover(Math.min(points.length - 1, Math.max(0, i)));
  };
  const shown = hover === null ? null : points[hover];
  // Held inside the tile at either end, which clips what reaches past it.
  let anchor = 'translateX(-50%)';
  if (hover !== null && hover / (points.length - 1) < 0.25) anchor = 'none';
  if (hover !== null && hover / (points.length - 1) > 0.75) {
    anchor = 'translateX(-100%)';
  }

  return (
    <div className={classes.box}>
      {shown && hover !== null && (
        <div
          className={classes.tip}
          style={{ left: `${(x(hover) / W) * 100}%`, transform: anchor }}
          aria-hidden
        >
          <b>{pct(shown.share)}</b> · {dayLabel(shown.day)} · {shown.met} of{' '}
          {shown.of}
        </div>
      )}
      <svg
        ref={svg}
        className={classes.svg}
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        aria-hidden
        onPointerMove={onMove}
        onPointerLeave={() => setHover(null)}
      >
        {hover !== null && (
          <line
            className={classes.hair}
            x1={x(hover)}
            x2={x(hover)}
            y1={0}
            y2={H}
          />
        )}
        <path className={classes.line} d={path} />
        <circle
          className={`${classes.now} ${classes.ring}`}
          cx={x(points.length - 1)}
          cy={y(last.share)}
          r={3.5}
        />
        {shown && hover !== null && hover !== points.length - 1 && (
          <circle
            className={`${classes.now} ${classes.ring}`}
            cx={x(hover)}
            cy={y(shown.share)}
            r={3.5}
          />
        )}
      </svg>
      <div className={classes.sub}>{change}</div>
      <table className={classes.hidden}>
        <caption>Repositories meeting every team rule, day by day</caption>
        <thead>
          <tr>
            <th scope="col">Day</th>
            <th scope="col">Share</th>
            <th scope="col">Repositories</th>
          </tr>
        </thead>
        <tbody>
          {points.map(p => (
            <tr key={p.day}>
              <th scope="row">{dayLabel(p.day)}</th>
              <td>{pct(p.share)}</td>
              <td>
                {p.met} of {p.of}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
